import { createContext, useContext, useState, useCallback } from 'react';
import { transactionAPI } from '../services/api';
import { getFinancialYears as getDefaultFinancialYears } from '../utils/constants';

const TransactionContext = createContext(null);

export function TransactionProvider({ children }) {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState(null);
  const [availableFinancialYears, setAvailableFinancialYears] = useState(getDefaultFinancialYears(3));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    pages: 0
  });

  const fetchTransactions = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await transactionAPI.getAll(params);
      // Filter out any null/undefined entries for safety
      setTransactions((data.data.transactions || []).filter(Boolean));
      setPagination(data.data.pagination);
      return data.data;
    } catch (err) {
      const errorMessage = err.error || 'Failed to fetch transactions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async (financialYear) => {
    try {
      setLoading(true);
      const { data } = await transactionAPI.getSummary(financialYear);
      setSummary(data.data.summary);
      return data.data;
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategoryBreakdown = useCallback(async (financialYear) => {
    try {
      const { data } = await transactionAPI.getCategoryBreakdown(financialYear);
      setCategoryBreakdown(data.data);
      return data.data;
    } catch (err) {
      console.error('Failed to fetch category breakdown:', err);
    }
  }, []);

  // Fetch user's distinct financial years and merge with defaults
  const fetchAvailableFinancialYears = useCallback(async () => {
    try {
      const { data } = await transactionAPI.getFinancialYears();
      const userYears = data.data.financialYears || [];
      const defaultYears = getDefaultFinancialYears(3);

      // Merge user years with default years
      // Get all unique year values
      const allYearValues = new Set([
        ...userYears,
        ...defaultYears.map(fy => fy.value)
      ]);

      // Convert to array of objects and sort descending
      const mergedYears = Array.from(allYearValues)
        .sort((a, b) => b.localeCompare(a))
        .map(value => ({
          value,
          label: `FY ${value}`
        }));

      setAvailableFinancialYears(mergedYears);
      return mergedYears;
    } catch (err) {
      console.error('Failed to fetch financial years:', err);
      // Keep default years on error
      return getDefaultFinancialYears(3);
    }
  }, []);

  const createTransaction = useCallback(async (transactionData) => {
    try {
      setError(null);
      const { data } = await transactionAPI.create(transactionData);

      // All transactions (including transfers) return single transaction
      if (data.data.transaction) {
        setTransactions((prev) => [data.data.transaction, ...prev.filter(Boolean)]);
      }
      return data.data.transaction;
    } catch (err) {
      const errorMessage = err.error || err.errors?.[0]?.message || 'Failed to create transaction';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const updateTransaction = useCallback(async (id, transactionData) => {
    try {
      setError(null);
      const { data } = await transactionAPI.update(id, transactionData);

      // All transactions (including transfers) return single transaction
      setTransactions((prev) =>
        prev.map((t) => (t._id === id ? data.data.transaction : t))
      );
      return data.data.transaction;
    } catch (err) {
      const errorMessage = err.error || 'Failed to update transaction';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const deleteTransaction = useCallback(async (id) => {
    try {
      setError(null);
      await transactionAPI.delete(id);
      setTransactions((prev) => prev.filter((t) => t && t._id !== id));
    } catch (err) {
      const errorMessage = err.error || 'Failed to delete transaction';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const value = {
    transactions,
    summary,
    categoryBreakdown,
    availableFinancialYears,
    loading,
    error,
    pagination,
    fetchTransactions,
    fetchSummary,
    fetchCategoryBreakdown,
    fetchAvailableFinancialYears,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    clearError: () => setError(null)
  };

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
}

export function useTransactions() {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransactions must be used within a TransactionProvider');
  }
  return context;
}
